import { useEffect, useRef, useState } from "react";
import {
  fetchActiveCountries,
  resolveTripVisaCountry,
  resolveDestinationVisa,
  buildVisaUrl,
} from "../../services/visaService";
import { trackEvent } from "../../utils/analytics";

const PassportIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M9 12a3 3 0 116 0 3 3 0 01-6 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M5 4.5A1.5 1.5 0 016.5 3h11A1.5 1.5 0 0119 4.5v15a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 015 19.5v-15z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M9 18h6"
    />
  </svg>
);

const ArrowIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const CheckIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

const TRUST_POINTS = [
  "Verified visa partner",
  "Fast turnaround",
  "Secure & paperless",
];

const SkeletonCard = () => (
  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 animate-pulse">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-gray-100" />
      <div className="flex-1 space-y-3">
        <div className="h-4 w-48 bg-gray-100 rounded" />
        <div className="h-3 w-72 bg-gray-100 rounded" />
        <div className="h-3 w-64 bg-gray-100 rounded" />
      </div>
    </div>
  </section>
);

const VisaCard = ({ match }) => {
  const visaUrl = buildVisaUrl(match.slug);
  if (!visaUrl) return null;

  return (
    <section className="relative bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500" />

      <div className="p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-700">
              <PassportIcon />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 leading-tight">
                  Visa for {match.name}
                </h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-semibold uppercase tracking-wider border border-teal-100">
                  Available
                </span>
              </div>
              <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                Apply for your {match.name} visa through our trusted partner — quick checks, transparent pricing, and end-to-end support.
              </p>
              <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                {TRUST_POINTS.map((point) => (
                  <li
                    key={point}
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-gray-600"
                  >
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-teal-50 text-teal-600">
                      <CheckIcon />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:shrink-0">
            <a
              href={visaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("visa_check_clicked", { destination: match.name })}
              className="group inline-flex w-full lg:w-auto items-center justify-center gap-2 rounded-full bg-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
            >
              <span>Check Visa & Apply</span>
              <ArrowIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <p className="mt-2 text-[11px] text-gray-400 text-center lg:text-right">
              Opens roammyway.visa2fly.com in a new tab
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const VisaSection = ({ tripData, onVisaStatusChange }) => {
  const [status, setStatus] = useState("loading");
  const [matches, setMatches] = useState([]);
  const cancelledRef = useRef(false);

  const isMultiDest = Array.isArray(tripData?.destinations) && tripData.destinations.length >= 2;

  useEffect(() => {
    cancelledRef.current = false;
    let active = true;

    const run = async () => {
      setStatus("loading");
      setMatches([]);

      if (!tripData?.destination && !tripData?.destinationData?.country && !isMultiDest) {
        if (active) { setStatus("hidden"); onVisaStatusChange?.(false); }
        return;
      }

      try {
        const countries = await fetchActiveCountries();
        if (!active || cancelledRef.current) return;
        if (!countries.length) { setStatus("hidden"); return; }

        let resolved;
        if (isMultiDest) {
          const results = await Promise.all(
            tripData.destinations.map(d => resolveDestinationVisa(d.name, countries))
          );
          const seen = new Set();
          resolved = results.filter(r => r && !seen.has(r.slug) && seen.add(r.slug));
        } else {
          const m = await resolveTripVisaCountry(tripData, countries);
          resolved = m ? [m] : [];
        }

        if (!active || cancelledRef.current) return;
        if (!resolved.length) { setStatus("hidden"); onVisaStatusChange?.(false); return; }
        setMatches(resolved);
        setStatus("ready");
        onVisaStatusChange?.(true);
      } catch {
        if (active) { setStatus("hidden"); onVisaStatusChange?.(false); }
      }
    };

    run();
    return () => {
      active = false;
      cancelledRef.current = true;
    };
  }, [tripData?.destination, tripData?.destinationData?.country, isMultiDest]);

  if (status === "hidden") return null;
  if (status === "loading") return <SkeletonCard />;
  if (!matches.length) return null;

  if (matches.length === 1) return <VisaCard match={matches[0]} />;

  return (
    <section className="relative bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500" />
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-700">
            <PassportIcon />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 leading-tight">Visa Requirements</h2>
            <p className="text-sm text-gray-500 mt-0.5">Apply through our trusted partner — quick, secure & paperless</p>
          </div>
        </div>
        <div className="space-y-3">
          {matches.map(match => {
            const visaUrl = buildVisaUrl(match.slug);
            if (!visaUrl) return null;
            return (
              <div key={match.slug} className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-semibold uppercase tracking-wider border border-teal-100 shrink-0">Available</span>
                  <span className="font-medium text-gray-900 text-sm truncate">{match.name}</span>
                </div>
                <a
                  href={visaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("visa_check_clicked", { destination: match.name })}
                  className="group inline-flex shrink-0 items-center gap-1.5 rounded-full bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-teal-700"
                >
                  Apply
                  <ArrowIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-[11px] text-gray-400 text-right">Opens roammyway.visa2fly.com in a new tab</p>
      </div>
    </section>
  );
};

export default VisaSection;
